import moment from 'moment-timezone';
import UserRoles from 'constants/UserRoles';

export const HELPDESK_CALL_FE_TYPE = 9;
export const HELPDESK_CALL_SNOOZE_STORAGE_KEY = 'helpdesk_call_banner_snooze';

export const seesHelpdeskCallBanner = (roles, mainRole) => {
	const roleValues = Array.isArray(roles) ? roles.map((role) => Number(role)) : [];
	return roleValues.includes(UserRoles.HelpDesk) && Number(mainRole) > UserRoles.Executive;
};

export const readHelpdeskCallSnoozeMap = () => {
	try {
		const raw = localStorage.getItem(HELPDESK_CALL_SNOOZE_STORAGE_KEY);
		if (!raw) {
			return {};
		}
		const parsed = JSON.parse(raw);
		return parsed && typeof parsed === 'object' ? parsed : {};
	} catch (e) {
		return {};
	}
};

export const writeHelpdeskCallSnoozeMap = (map) => {
	try {
		localStorage.setItem(HELPDESK_CALL_SNOOZE_STORAGE_KEY, JSON.stringify(map));
	} catch (e) {
		// ignore quota / private mode failures
	}
};

export const clearHelpdeskCallSnooze = (engineerPkey) => {
	if (engineerPkey == null) {
		return readHelpdeskCallSnoozeMap();
	}
	const nextMap = {...readHelpdeskCallSnoozeMap()};
	delete nextMap[String(engineerPkey)];
	writeHelpdeskCallSnoozeMap(nextMap);
	return nextMap;
};

export const parseNotificationPayload = (notification) => {
	const data = notification?.Data ?? notification?.data;
	if (data == null || data === '' || data === '{}') {
		return null;
	}
	if (typeof data === 'object') {
		return data;
	}
	try {
		return JSON.parse(data);
	} catch (e) {
		return null;
	}
};

export const getEngineerPkey = (notification) => {
	const payload = parseNotificationPayload(notification);
	const pkey = payload?.pkey ?? payload?.data?.engineer_pkey;
	return pkey != null ? Number(pkey) : null;
};

export const getEngineerName = (notification) => {
	const payload = parseNotificationPayload(notification);
	const name = payload?.data?.engineer_name;
	if (name) {
		return String(name).trim();
	}
	return notification?.NotificationTypes?.Message
		?? notification?.notificationTypes?.Message
		?? 'Field Engineer';
};

export const getLastContacted = (notification) => {
	const payload = parseNotificationPayload(notification);
	return payload?.data?.last_contacted ?? null;
};

export const getEngineerPhone = (notification) => {
	const payload = parseNotificationPayload(notification);
	const phone = payload?.data?.phone_cell ?? payload?.data?.phone ?? null;
	if (phone == null) {
		return '';
	}
	return String(phone).trim();
};

export const formatPhoneNumber = (phone) => {
	const digits = String(phone ?? '').replace(/\D/g, '');
	if (digits.length === 10) {
		return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
	}
	if (digits.length === 11 && digits.startsWith('1')) {
		return `${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
	}
	return String(phone ?? '').trim();
};

export const getLastContactedLabel = (notification) => {
	const lastContacted = getLastContacted(notification);
	if (lastContacted) {
		return `Last contacted ${moment(lastContacted).format('MMM D, YYYY h:mm A')}`;
	}
	return parseNotificationPayload(notification) ? 'Never contacted' : '';
};

const getCreatedTs = (notification) => {
	const created = notification?.Created ?? notification?.created;
	const ts = created ? moment(created).valueOf() : 0;
	return Number.isFinite(ts) ? ts : 0;
};

export const getCallRowKey = (notification, index) => {
	const pkey = getEngineerPkey(notification);
	if (pkey != null && !Number.isNaN(pkey)) {
		return String(pkey);
	}
	return String(notification?.Pkey ?? notification?.pkey ?? index);
};

/** Outstanding type-9 reminders, oldest first. Does not apply the banner snooze. */
export const buildHelpdeskCallQueue = (notifications) => {
	return (notifications || [])
		.filter((item) => Number(item.Type ?? item.type) === HELPDESK_CALL_FE_TYPE)
		.filter((item) => {
			const engineerPkey = getEngineerPkey(item);
			return engineerPkey != null && !Number.isNaN(engineerPkey);
		})
		.slice()
		.sort((a, b) => getCreatedTs(a) - getCreatedTs(b));
};

export const applyHelpdeskCallSnooze = (queue, snoozeMap, now) => {
	return (queue || []).filter((item) => {
		const engineerPkey = getEngineerPkey(item);
		if (engineerPkey == null || Number.isNaN(engineerPkey)) {
			return true;
		}
		const until = Number(snoozeMap?.[String(engineerPkey)] ?? 0);
		return !(until > now);
	});
};
